import React, { useState, useMemo, useCallback, useEffect } from 'react';
import copy from 'copy-to-clipboard';
import { useSelector } from 'react-redux';
import { Button, Checkbox, FormControlLabel, TextField, Typography, Box, CircularProgress, Icon, Autocomplete, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import Modal from 'global/components/modal';
import { createShareLink } from './createShareLink';
import { getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
import { get } from 'global/lib/lodash';
import { stringObj } from 'global/utils';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useUser } from 'global/hooks/useUser';
import sendData from 'redux/actions/sendData';

// PFM-2564 — Share button modal. Lets the current user produce a shareable URL
// or send it via email to a list of recipients.
//
// Two flavors of "link":
//   - Live link (anyone with the link, via hashed auth) — created on demand here
//     by POSTing /share-hashes + /shareables.
//   - Authenticated URL (window.location.href) — only useful to recipients who
//     already have a normal account + access to the resource.
//
// The "Anyone with the link can view" checkbox toggles between these two.
// Its default + visibility honors the builder's "Allow Link Sharing" setting.
//
// Recipients are chosen via a chip+autocomplete:
//   - Internal users for assessments/tasks come from the realm-scoped user list
//     used elsewhere; for documents (Library) we fall back to ALL users.
//   - External contacts come from the /contacts query endpoint (PFM-2564).
//   - Free-text typing is allowed only when "Anyone with the link" is checked;
//     in that case the entry is treated as a new external contact (created
//     idempotently via POST /contacts on send).
//
// Email send routes through POST /messages with the appropriate `message_type`
// (`assessment_share` / `task_share` / `edition_document_share`),
// `meta.anonymous_view` driven by the checkbox, and a `contacts` relationship
// for external recipients.

const CheckboxRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(1),
}));

const ExpiryHelper = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
  marginLeft: theme.spacing(4),
  marginTop: `calc(${theme.spacing(0.5)} * -1)`,
  fontStyle: 'italic',
}));

const Helper = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
}));

const OutsideHelper = styled(Typography)(({ theme }) => ({
  color: theme.palette.warning.dark,
  fontSize: '0.8rem',
  fontStyle: 'italic',
  marginTop: theme.spacing(1),
}));

const recipientFieldSx = {
  width: '100%',
  marginTop: 2,
};

// PFM-2547: mockups place Copy Link on the left and Send on the right.
const Actions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(2),
  },
}));

const CopyCluster = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(0.5),
}));

const CopyStatus = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.8rem',
  marginLeft: theme.spacing(0.5),
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  marginTop: theme.spacing(1),
  fontSize: '0.85rem',
}));

const CopyButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'diminished',
})(({ diminished }) => ({
  ...(diminished ? { opacity: 0.55 } : null),
}));

const formatExpiry = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + Number(days || 7));
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};

const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// scope → (model_type, message_type) per contacts.md and ticket AC.
// NOTE (PFM-2648): the task `messageType` here is the DEFAULT (`task_share`,
// the normal PDF-generating share). When "Anyone with the link" is checked,
// onSend overrides the task message_type to `task_hash_share` (hashed/anonymous
// link share). Document/assessment types are not affected by share mode.
const SCOPE_TO_MESSAGE = {
  assessment: { modelType: 'assessment', messageType: 'assessment_share' },
  task: { modelType: 'task', messageType: 'task_share' },
  document: { modelType: 'edition_document', messageType: 'edition_document_share' },
};

// Each option in the Autocomplete is shaped { kind: 'user' | 'contact' | 'new', id, email, name, label }.
const optionLabel = (opt) => {
  if (!opt) return '';
  if (typeof opt === 'string') return opt;
  return opt.label || opt.name || opt.email || '';
};

const optionKey = (opt) => {
  if (!opt) return '';
  return `${opt.kind || 'new'}:${opt.id || opt.email || ''}`;
};

// scope: { kind, id, courseId? }
// title: human label like "Lot a photos and captions" for the modal headline
// onSent: optional callback fired after a successful Send (before the modal
//   closes) so the host screen can surface a confirmation toast — PFM-2648 asks
//   for a "Share email sent" confirmation like the Notify Assignees banner.
const ShareLinkModal = ({ open, onClose, scope, title, onSent }) => {
  // useSelector subscribes us to auth.accountSettings changes; the actual lookup
  // goes through getAccountSetting (which reads from store directly).
  const accountSettings = useSelector((s) => s?.auth?.accountSettings);
  // PFM-2646: all share-link policy lives in the single "Link Sharing" blob:
  // { default: { enabled, expiration_days, allow_mode, allowed_domains } }.
  // getAccountSetting returns {} when the setting isn't present (parseJson default).
  const ls = getAccountSetting(SETTINGS.linkSharing);
  const cfg = (ls && ls.default) || {};
  const allowMode = typeof cfg.allow_mode === 'string' ? cfg.allow_mode : cfg.enabled ? 'allowed-default-on' : 'not-allowed';
  const defaultDays = Number(cfg.expiration_days != null ? cfg.expiration_days : 7) || 7;
  // Reference accountSettings so the lint rule + reactivity behave correctly.
  // eslint-disable-next-line no-unused-vars
  const _settingsTouch = accountSettings;
  const { siteContextSelectedRealm } = useUser();

  const liveEnabled = allowMode !== 'not-allowed';
  const liveDefault = allowMode === 'allowed-default-on';

  const [anyoneWithLink, setAnyoneWithLink] = useState(liveDefault && liveEnabled);
  const [recipients, setRecipients] = useState([]);
  const [inputValue, setInputValue] = useState('');
  // PFM-2673: contacts created via POST /contacts during this modal session.
  // The /contacts query is client-cached, so a freshly-saved email won't appear
  // in the autocomplete (it would still be offered as "Add ...") until a refetch.
  // We seed it into the options list immediately, deduped by lowercased email.
  const [sessionContacts, setSessionContacts] = useState([]);
  // Explicit Autocomplete open state — MUI's internal state proved unreliable
  // here when the surrounding page churns (Pusher reconnects trigger redux
  // updates → parent re-renders that occasionally reset MUI's internal
  // `open` to false even while the input is focused with a non-empty value).
  // Owning this state ourselves keeps the popper visible while the user types.
  const [acOpen, setAcOpen] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // PFM-2547: split feedback so "Link copied" appears under Copy Link and
  // "Sent to N recipients" appears elsewhere (per failed-testing comment).
  const [copyMsg, setCopyMsg] = useState(null);
  const [sendMsg, setSendMsg] = useState(null);

  // Realm to filter user/contact lookups against. For library/document we don't
  // filter — the ticket spec says "Library uses ALL users".
  const realmId = get(siteContextSelectedRealm, 'id');
  const isDocument = scope?.kind === 'document';

  // Users — for assessment/task: prefer realm-scoped (matches StatusBar/TaskDetail
  // pattern). For document, OR when we can't determine the realm (e.g. the user
  // hasn't selected a site-context realm), fall back to unscoped so the dropdown
  // still surfaces options.
  const useRealmFilter = !isDocument && !!realmId;
  const usersResult = useRetrieveData(
    { url: '/users' },
    {
      waitFor: open,
      build: ['users'],
      params: useRealmFilter ? { filter: { content_realm_id: realmId } } : {},
      useCache: { reload: 1 },
    }
  );

  // Contacts query — filter by realm where available so we surface relevant ones.
  const contactsResult = useRetrieveData(
    { url: '/contacts' },
    {
      waitFor: open,
      build: ['contacts'],
      params: useRealmFilter ? { filter: { realm: realmId }, sort: '-created_at' } : { sort: '-created_at' },
      // PFM-2673: reload: -1 makes shouldReload() always true (timeGreaterThen
      // diff > -1 is true even for a 0-minute-old cache), so each modal open
      // refetches /contacts. This keeps a contact saved in one assessment's Share
      // modal visible the next time the modal opens elsewhere — without waiting
      // for the previous 1-minute TTL to lapse. Initial state still seeds from the
      // cached copy (useRetrieveData line ~105) so there's no empty flash.
      useCache: { reload: -1 },
    }
  );

  const userOptions = useMemo(() => {
    return (get(usersResult, 'data', []) || [])
      .filter((u) => !!get(u, 'email'))
      .map((u) => ({
        kind: 'user',
        id: u.id,
        email: u.email,
        name: u.name,
        label: u.name || u.email,
      }));
  }, [usersResult]);

  const contactOptions = useMemo(() => {
    return (get(contactsResult, 'data', []) || [])
      .filter((c) => !!get(c, 'email'))
      .map((c) => ({
        kind: 'contact',
        id: c.id,
        email: c.email,
        name: [c.firstName || c['first-name'] || c.first_name, c.lastName || c['last-name'] || c.last_name].filter(Boolean).join(' '),
        label: c.email,
      }));
  }, [contactsResult]);

  // Combined autocomplete options — users first, then contacts not already in
  // users, then any contacts created this session that aren't already present.
  // PFM-2673: session contacts make a just-saved email appear immediately as a
  // saved contact (removing the duplicate "Add ..." affordance) before the
  // cached /contacts query has refreshed.
  const allOptions = useMemo(() => {
    const seenEmails = new Set(userOptions.map((u) => (u.email || '').toLowerCase()));
    const dedupedContacts = contactOptions.filter((c) => !seenEmails.has((c.email || '').toLowerCase()));
    dedupedContacts.forEach((c) => seenEmails.add((c.email || '').toLowerCase()));
    const dedupedSession = sessionContacts.filter((c) => !seenEmails.has((c.email || '').toLowerCase()));
    return [...userOptions, ...dedupedContacts, ...dedupedSession];
  }, [userOptions, contactOptions, sessionContacts]);

  const hasRecipients = recipients.length > 0;
  // External recipients are anything that's NOT a known user (contacts + freshly-typed new emails).
  const externalRecipients = useMemo(
    () => recipients.filter((r) => r && r.kind !== 'user'),
    [recipients]
  );
  const hasExternal = externalRecipients.length > 0;

  const resolveUrl = useCallback(async () => {
    if (anyoneWithLink) {
      if (generatedUrl) return generatedUrl;
      const result = await createShareLink(scope, { expirationDays: defaultDays });
      setGeneratedUrl(result.url);
      return result.url;
    }
    return window.location.href;
  }, [anyoneWithLink, generatedUrl, scope, defaultDays]);

  // PFM-2642: Safari/WebKit only honors clipboard writes while the page still
  // has transient user activation from the click. createShareLink() performs two
  // awaited network POSTs, and any await before navigator.clipboard.writeText()
  // discards Safari's activation, causing NotAllowedError. To keep the clipboard
  // write inside the synchronous gesture window, pre-generate the live link as
  // soon as "Anyone with the link" is active so the URL is ready (cached in
  // generatedUrl) by the time Copy Link is clicked.
  useEffect(() => {
    if (!open || !anyoneWithLink || generatedUrl) return;
    let cancelled = false;
    createShareLink(scope, { expirationDays: defaultDays })
      .then((result) => {
        if (!cancelled) setGeneratedUrl(result.url);
      })
      .catch(() => {
        // Swallow here; onCopy/onSend will surface the error when the user acts.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anyoneWithLink]);

  const onCopy = async () => {
    setError(null);
    setCopyMsg(null);
    setSendMsg(null);
    // Fast path: the live link is already generated (pre-fetched on toggle/open)
    // or we're copying the authenticated URL. Either way there is NO await before
    // the clipboard write, so Safari keeps user activation. Use the synchronous
    // copy-to-clipboard helper (execCommand-based) which the codebase standardizes
    // on and which is reliable across browsers including iOS Safari.
    if (!anyoneWithLink || generatedUrl) {
      try {
        const url = anyoneWithLink ? generatedUrl : window.location.href;
        copy(url);
        setCopyMsg('Link copied to clipboard');
      } catch (e) {
        setError(get(e, 'message', 'Could not copy link'));
      }
      return;
    }
    // Fallback: link not yet generated (e.g. the pre-fetch is still in flight).
    // Generate then copy synchronously with the same helper.
    setBusy(true);
    try {
      const url = await resolveUrl();
      copy(url);
      setCopyMsg('Link copied to clipboard');
    } catch (e) {
      setError(get(e, 'message', 'Could not generate or copy link'));
    } finally {
      setBusy(false);
    }
  };

  // Idempotently create-or-fetch a contact by email. Per contacts.md, the API
  // returns the existing record (with attached realms/users merged in) when
  // the email already exists, so we can blindly POST.
  const ensureContact = async (email) => {
    const attrs = { email };
    const relationships = {};
    if (realmId) {
      relationships.realms = { data: [{ type: 'realms', id: String(realmId) }] };
    }
    const resp = await sendData({
      url: 'contacts',
      method: 'POST',
      data: {
        data: {
          type: 'contacts',
          attributes: attrs,
          ...(Object.keys(relationships).length ? { relationships } : {}),
        },
      },
    });
    const id = get(resp, 'data.data.id');
    if (!id) throw new Error('contacts: backend did not return an id');
    // PFM-2673: remember the new contact so it surfaces in the autocomplete as a
    // saved contact right away (no refresh). Mirror the contactOptions shape so
    // allOptions/filterOptions treat it like any other contact. Dedupe by
    // lowercased email so the same address is never offered/added twice.
    const lowerEmail = (email || '').toLowerCase();
    setSessionContacts((prev) =>
      prev.some((c) => (c.email || '').toLowerCase() === lowerEmail)
        ? prev
        : [...prev, { kind: 'contact', id, email, name: '', label: email }]
    );
    // PFM-2673 (failed-testing fix): seed the freshly-created contact directly
    // into the redux /contacts cache NOW, while still mounted. resetState() on
    // close wipes sessionContacts, and the modal is conditionally mounted
    // ({open && <ShareLinkModal/>}) so it UNMOUNTS — meaning the next open seeds
    // its contacts state from the redux cache (useRetrieveData line ~105). A
    // fire-and-forget update() can lose the race with that unmount, so the new
    // email would still show as Add "…" until a page refresh. Writing the
    // contact into the cache via manuallySetData persists it synchronously, so
    // the next open (even on a different assessment/task) sees it as a saved
    // External contact. Shape mirrors what contactOptions reads (id/email +
    // name parts). Dedupe by lowercased email so we never duplicate a cache row.
    const cached = get(contactsResult, 'data', []) || [];
    if (!cached.some((c) => (get(c, 'email') || '').toLowerCase() === lowerEmail)) {
      contactsResult.manuallySetData([{ id: String(id), email, 'first-name': '', 'last-name': '' }, ...cached]);
    }
    return id;
  };

  // PFM-2647: collapse the modal's reset so we can reuse it for both the Modal's
  // own onClose and the new auto-close after a successful Send (per Samuel's
  // ticket comment: "We should close the modal after Send").
  const resetState = useCallback(() => {
    setGeneratedUrl(null);
    setRecipients([]);
    setInputValue('');
    setError(null);
    setCopyMsg(null);
    setSendMsg(null);
    setAcOpen(false);
    // PFM-2673: clear session contacts on reset; the refreshed /contacts cache
    // (reload: -1 refetch) becomes the source of truth on the next open.
    setSessionContacts([]);
  }, []);

  const onSend = async () => {
    setError(null);
    setCopyMsg(null);
    setSendMsg(null);
    if (!hasRecipients) {
      setError('Add at least one recipient.');
      return;
    }
    // Validate any freshly-typed entries.
    const invalid = recipients.find((r) => r.kind === 'new' && !isValidEmail(r.email));
    if (invalid) {
      setError(`Invalid email: ${invalid.email}`);
      return;
    }
    // External recipients can only receive email when "Anyone with the link" is checked
    // (contacts.md: emails only flow when meta.anonymous_view === true).
    if (hasExternal && !anyoneWithLink) {
      setError('External recipients can only receive a link when "Anyone with the link can view" is checked.');
      return;
    }

    setBusy(true);
    let sent = false;
    try {
      // Resolve the link we want recipients to land on. Same branch as Copy Link:
      //   - "Anyone with the link" → the live, hashed share URL (works without an
      //     account); resolveUrl() creates the share-hash + shareable as needed.
      //   - otherwise → the authenticated page URL (the Share button always lives
      //     on the shared content's page, so href is the canonical content link).
      const shareUrl = anyoneWithLink ? await resolveUrl() : window.location.href;

      // Step 1: ensure every external recipient has a contact id.
      const externalIds = [];
      for (const r of externalRecipients) {
        if (r.kind === 'contact' && r.id) {
          externalIds.push(String(r.id));
        } else {
          const cid = await ensureContact(r.email);
          externalIds.push(String(cid));
        }
      }

      // Step 2: build the message POST.
      const map = SCOPE_TO_MESSAGE[scope.kind] || SCOPE_TO_MESSAGE.assessment;
      const userRecipientIds = recipients.filter((r) => r.kind === 'user').map((r) => r.id);
      const notifyUsers = userRecipientIds.length > 0;
      const anonymousView = !!anyoneWithLink;

      const relationships = {};
      if (userRecipientIds.length) {
        relationships.users = { data: userRecipientIds.map((uid) => ({ type: 'users', id: String(uid) })) };
      }
      if (externalIds.length) {
        relationships.contacts = { data: externalIds.map((cid) => ({ type: 'contacts', id: String(cid) })) };
      }

      // PFM-2648 (follow-up): scope-aware target wiring.
      //   - task: the backend's /messages endpoint does NOT accept the task via
      //     attributes.model_id — it expects the task as a JSON:API relationship
      //     (relationships.tasks). This mirrors the proven-working task-share
      //     sender in feature/task/TaskNotify/ShareModal.js, which omits
      //     model_id and passes data: [{ type: 'tasks', id }]. Including model_id
      //     for task scope causes the 500.
      //   - document / assessment: keep the model_id path — confirmed working
      //     (document → model_type "edition_document"; assessment matches
      //     AssessmentScore/TaskBanner.js which sends model_type "assessment"
      //     + model_id, NOT a relationship).
      const isTaskScope = scope.kind === 'task';
      if (isTaskScope) {
        relationships.tasks = { data: [{ type: 'tasks', id: String(scope.id) }] };
      }

      // PFM-2648 (latest failed-testing comment): the backend split Task Share
      // into two message types. A normal PDF-generating task share is
      // `task_share` (the SCOPE_TO_MESSAGE default); a hashed-link
      // "anyone-with-the-link" task share is `task_hash_share`. Only the task
      // scope distinguishes these — document (`edition_document_share`) and
      // assessment (`assessment_share`) are unchanged. Select the hashed type
      // for task scope when "Anyone with the link can view" is checked.
      const messageType = isTaskScope && anonymousView ? 'task_hash_share' : map.messageType;

      // stringObj stringifies all top-level numeric attributes (model_id,
      // realm_id, status) to match every working /messages sender in the app
      // (e.g. AssessmentScore/TaskBanner.js). The backend rejects numeric
      // model_id/realm_id — this was the PFM-2648 send failure. stringObj only
      // touches top-level numbers, so the nested `meta` object and string
      // fields below are left intact.
      // PFM-2648: the email template renders `body` as raw HTML, so supply the
      // correct destination link here. Without this the email only shows the
      // backend's hardcoded "Link to pdf is…" line, which points at the generated
      // PDF instead of the shared content. The link differs by share mode (live
      // hash vs. authenticated URL), which the backend can't know — so the
      // frontend, which does, provides it. `&` in query strings survives JSON
      // transport because the template html_entity_decode()s the body.
      const linkLabel = title ? `Open “${title}” in PERFORM` : 'Open in PERFORM';
      const body = shareUrl ? `${linkLabel}: <a href="${shareUrl}">${shareUrl}</a>` : '';

      const attributes = stringObj({
        message_type: messageType,
        model_type: map.modelType,
        // task scope conveys its target via relationships.tasks (above); the
        // backend rejects model_id for tasks, so omit it for task scope only.
        ...(isTaskScope ? {} : { model_id: Number(scope.id) }),
        realm_id: realmId,
        status: 1,
        title: title ? `Share: ${title}` : `Share`,
        body,
        to: '',
        meta: {
          notify_users: notifyUsers,
          anonymous_view: anonymousView,
        },
      });

      // PFM-2647: /messages is a non-idempotent write — the backend queues an
      // email on success. sendData() auto-retries non-4xx failures up to
      // maxTries(3), so a slow or 5xx response that had ALREADY sent the email
      // gets RE-SENT on retry, producing the "two emails" reported in the ticket.
      // Force a single attempt for this send; a genuine failure surfaces an error
      // and the user can retry deliberately.
      await sendData(
        {
          url: '/messages',
          method: 'post',
          data: {
            data: {
              type: 'messages',
              attributes,
              relationships,
            },
          },
        },
        { maxTries: 0 }
      );

      sent = true;
    } catch (e) {
      setError(get(e, 'message', 'Could not send'));
    } finally {
      setBusy(false);
    }

    // PFM-2647: close the modal after a successful Send (per ticket). Closing
    // also removes the second duplicate-email vector — a confused user clicking
    // Send again because the modal stayed open. Run after the finally so
    // setBusy(false) lands while still mounted (some callers unmount on close).
    if (sent) {
      // PFM-2648: the modal closes on success, so the "email sent" confirmation
      // can't live inside it. Hand a ready-made message up to the host screen,
      // which keeps a SnackAlerts mounted to show the toast (mirrors the Notify
      // Assignees confirmation, minus the re-open button). Capture the recipient
      // count BEFORE resetState() wipes `recipients`.
      const count = recipients.length;
      if (typeof onSent === 'function') {
        onSent({
          count,
          message: `Share email sent to ${count} recipient${count === 1 ? '' : 's'}.`,
        });
      }
      // PFM-2673: refresh the /contacts cache so any contacts created during this
      // send are persisted into the redux-backed cache with their canonical
      // backend records (realm/name merges). Fire-and-forget — the modal closes
      // now, and the refreshed cache backs the next open. update() bypasses the
      // cache gate (it sets a fresh currentOpts token), unlike a passive read.
      contactsResult.update();
      resetState();
      onClose();
    }
  };

  const copyDisabled = busy;
  const sendDisabled = busy || !hasRecipients;

  // Build new-email option when input looks like a valid email and freeform is allowed.
  const freeformAllowed = anyoneWithLink;
  const filterOptions = (opts, params) => {
    const input = (params.inputValue || '').trim().toLowerCase();
    const filtered = opts.filter((o) => {
      if (!input) return true;
      return (o.email || '').toLowerCase().includes(input) || (o.name || '').toLowerCase().includes(input);
    });
    // Append a "add as new" option when freeform allowed and input is an email not in list.
    if (freeformAllowed && isValidEmail(input)) {
      const exists = filtered.some((o) => (o.email || '').toLowerCase() === input);
      if (!exists) {
        filtered.push({
          kind: 'new',
          id: null,
          email: input,
          name: input,
          // label is the chip display once selected (just the email);
          // dropdown menu adds the "Add ..." affordance via renderOption.
          label: input,
        });
      }
    }
    return filtered;
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (busy) return;
        resetState();
        onClose();
      }}
      title={title ? `Share: "${title}"` : `Share ${scope?.kind || ''}`.trim()}
      paperMaxWidth={600}
    >
      <Box
        data-testid="share-link-modal"
        sx={{ minWidth: { xs: '100%', sm: 420 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}
      >
        <Autocomplete
          sx={recipientFieldSx}
          multiple
          // PFM-2673 (BUG 2): hide already-selected recipients from the dropdown so
          // the same option can't be clicked twice. MUI's multiple-select TOGGLES an
          // option on re-click, so re-selecting an already-chosen recipient would
          // remove it and clear the selection ("everything disappears"). Filtering
          // selected options out (matched by email via isOptionEqualToValue) makes a
          // second selection impossible. Works as a pre-filter before filterOptions.
          filterSelectedOptions
          options={allOptions}
          value={recipients}
          inputValue={inputValue}
          // Force-open whenever there's input text — the modal lives in a tree
          // that re-renders constantly (Pusher reconnects), and MUI's internal
          // open state cannot be trusted to stay true between typing and
          // render. Deriving from inputValue makes "popper visible while
          // typing" the source of truth.
          open={!busy && (acOpen || !!inputValue)}
          onOpen={() => setAcOpen(true)}
          onClose={(_e, reason) => {
            if (reason === 'blur' && inputValue) return;
            setAcOpen(false);
          }}
          onInputChange={(_e, val, reason) => {
            // Don't clobber typing on selection (Autocomplete resets to '' by default).
            if (reason === 'reset') return;
            setInputValue(val);
            if (val) setAcOpen(true);
          }}
          onChange={(_e, vals) => {
            // PFM-2673 (BUG 2): De-dupe primarily by EMAIL (case-insensitive),
            // falling back to optionKey for entries lacking an email. Because the
            // same address can arrive as both a typed kind:'new' option and a
            // saved kind:'contact' option, deduping by kind+id alone would let a
            // single email appear twice and desync MUI's selection (chips +
            // options vanish). Email-first identity collapses these to one chip
            // and makes re-selecting an already-present recipient a no-op.
            const seenKeys = new Set();
            const seenEmails = new Set();
            const next = [];
            (vals || []).forEach((v) => {
              if (!v) return;
              // string entries come through when freeSolo and user hits Enter on raw text
              if (typeof v === 'string') {
                const email = v.trim().toLowerCase();
                if (!email || seenEmails.has(email)) return;
                if (!isValidEmail(email)) return;
                if (!freeformAllowed) return;
                seenEmails.add(email);
                next.push({ kind: 'new', id: null, email, name: email, label: email });
                return;
              }
              const em = (v.email || '').toLowerCase();
              if (em) {
                if (seenEmails.has(em)) return;
                seenEmails.add(em);
                next.push(v);
                return;
              }
              const key = optionKey(v);
              if (seenKeys.has(key)) return;
              seenKeys.add(key);
              next.push(v);
            });
            setRecipients(next);
            setInputValue('');
            setError(null);
            setCopyMsg(null);
            setSendMsg(null);
          }}
          getOptionLabel={optionLabel}
          // PFM-2673 (BUG 2): identity by EMAIL (case-insensitive) first, then
          // fall back to optionKey. The same address can exist as a typed
          // kind:'new' option (key new:<email>) AND a saved kind:'contact'
          // option (key contact:<id>). Comparing by optionKey alone treats them
          // as different, so MUI's multiple-select sees a value that isn't in
          // options → it desyncs and the chips + dropdown options all vanish.
          // Matching on email keeps a single stable identity for one address.
          isOptionEqualToValue={(a, b) => {
            const ae = (a && a.email ? a.email : '').toLowerCase();
            const be = (b && b.email ? b.email : '').toLowerCase();
            if (ae && be) return ae === be;
            return optionKey(a) === optionKey(b);
          }}
          filterOptions={filterOptions}
          freeSolo={freeformAllowed}
          disabled={busy}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((opt, index) => {
              const isExternal = opt.kind !== 'user';
              const { key, ...rest } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  size="small"
                  color={isExternal ? 'warning' : 'default'}
                  label={opt.name || opt.email}
                  {...rest}
                  data-testid={`share-link-chip-${opt.kind}`}
                />
              );
            })
          }
          renderOption={(props, opt) => (
            <li {...props} key={optionKey(opt)} data-testid={`share-link-option-${opt.kind}`}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2">{opt.kind === 'new' ? `Add "${opt.email}"` : opt.label}</Typography>
                {opt.email && opt.kind !== 'new' && opt.email !== opt.label && (
                  <Typography variant="caption" color="text.secondary">{opt.email}</Typography>
                )}
                {opt.kind === 'contact' && (
                  <Typography variant="caption" color="text.secondary">External contact</Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Send To"
              placeholder={freeformAllowed ? 'Search users or type an email' : 'Search users'}
              data-testid="share-link-emails-input"
            />
          )}
        />

        {hasExternal && !anyoneWithLink && (
          <OutsideHelper data-testid="share-link-external-warn">
            Sharing to users outside your organization — enable "Anyone with the link" to send.
          </OutsideHelper>
        )}
        {hasExternal && anyoneWithLink && (
          <OutsideHelper data-testid="share-link-external-helper">
            Sharing to users outside your organization
          </OutsideHelper>
        )}

        {liveEnabled ? (
          <CheckboxRow>
            <FormControlLabel
              sx={{ ml: 0 }}
              control={
                <Checkbox
                  checked={anyoneWithLink}
                  // When external recipients are present we don't let the user
                  // turn anonymous_view OFF — they wouldn't get the email.
                  disabled={hasExternal}
                  onChange={(e) => {
                    setAnyoneWithLink(e.target.checked);
                    setGeneratedUrl(null);
                    setCopyMsg(null);
                  }}
                  data-testid="share-link-anyone-checkbox"
                />
              }
              label="Anyone with the link can view"
            />
            <ExpiryHelper data-testid="share-link-helper">
              {anyoneWithLink
                ? `Expires in ${defaultDays === 1 ? 'one day' : `${defaultDays} days`}: ${formatExpiry(defaultDays)}`
                : 'Only users with accounts can open'}
            </ExpiryHelper>
          </CheckboxRow>
        ) : (
          <Helper>
            Live link sharing is disabled by your administrator. Only users in your organization with access can open this link.
          </Helper>
        )}

        {error && <ErrorText data-testid="share-link-error">{error}</ErrorText>}

        <Actions>
          <CopyCluster>
            <CopyButton
              variant="outlined"
              color="primary"
              onClick={onCopy}
              disabled={copyDisabled}
              startIcon={busy && !hasRecipients ? <CircularProgress size={16} /> : <Icon>link</Icon>}
              diminished={hasRecipients}
              data-testid="share-link-copy-btn"
            >
              Copy Link
            </CopyButton>
            {copyMsg && <CopyStatus data-testid="share-link-status">{copyMsg}</CopyStatus>}
          </CopyCluster>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={onSend}
              disabled={sendDisabled}
              startIcon={busy && hasRecipients ? <CircularProgress size={16} /> : <Icon>send</Icon>}
              data-testid="share-link-send-btn"
            >
              Send
            </Button>
            {sendMsg && <CopyStatus data-testid="share-link-send-status">{sendMsg}</CopyStatus>}
          </Box>
        </Actions>
      </Box>
    </Modal>
  );
};

export default ShareLinkModal;
